// Phase 5 (WebAuthn PRF passkey) of docs/bao-mat-ban-web-va-passkey-vi.md —
// mobile-web only, milestone 1: the passkey service.
//
// This module derives a STABLE secret from a platform/hardware authenticator via
// the WebAuthn PRF (`hmac-secret`) extension. That secret wraps the keystore
// `mainKey` as an additional secret (`keystore.addSecret('passkey', prfKey, …)`),
// exactly like the existing `password`/`biometrics` secrets — the keystore secret
// id is a plain string, so no `ambire-common` change is needed.
//
// Why page-only: `navigator.credentials` needs a DOM + a secure context, neither
// of which exists in the keystore Worker. So the PRF is derived HERE on the page
// and only the resulting `prfKey` (a transient hex string) is handed to the
// keystore — the same trust boundary the password already crosses. The PRF secret
// is NEVER persisted; only the non-secret `{credentialId, salt}` handle is stored
// so unlock can re-derive the key from the hardware.
//
// IMPORTANT: a passkey used as a mere login gate (no PRF) is useless against
// page-context XSS (see §3 of the plan). PRF is mandatory — `getPasskeyPrf` throws
// if the authenticator doesn't return a PRF result.

const RP_NAME = 'ZKX Wallet'

// Storage key for the non-secret handle. The handle (credentialId + salt) is NOT
// sensitive: it only identifies which credential to ask and with which salt; the
// derived key still requires the physical authenticator + user verification.
const PASSKEY_HANDLE_STORAGE_KEY = 'zkx-passkey-handle'

// The keystore secret id this passkey registers under (alongside 'password').
export const PASSKEY_SECRET_ID = 'passkey'

export type PasskeyHandle = {
  // base64url of the credential's rawId.
  credentialId: string
  // hex of the random 32-byte PRF salt chosen at registration. Stable per handle.
  salt: string
}

// --- byte helpers -----------------------------------------------------------

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2)
  for (let i = 0; i < out.length; i += 1) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  return out
}

function bufToB64url(buf: ArrayBuffer): string {
  const bin = Array.from(new Uint8Array(buf), (b) => String.fromCharCode(b)).join('')
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function b64urlToBuf(b64url: string): ArrayBuffer {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
  const bin = atob(b64 + pad)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i)
  return out.buffer
}

// Cast to BufferSource: under TS 5.7's generic `Uint8Array<ArrayBufferLike>` the
// WebAuthn option types (`BufferSource`) don't accept it directly, though it is a
// valid view at runtime.
function asBufferSource(bytes: Uint8Array): BufferSource {
  return bytes as unknown as BufferSource
}

function utf8ToBytes(str: string): Uint8Array {
  return new TextEncoder().encode(str)
}

// --- support detection ------------------------------------------------------

/**
 * Coarse capability check: the WebAuthn API exists AND we're in a secure context.
 * Actual PRF availability depends on the authenticator and can only be confirmed
 * by attempting the ceremony — `getPasskeyPrf` throws if PRF is absent at runtime.
 */
export function isPasskeyPrfSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!window.PublicKeyCredential &&
    !!navigator.credentials &&
    window.isSecureContext === true
  )
}

// --- handle persistence (non-secret) ----------------------------------------

export function loadPasskeyHandle(): PasskeyHandle | null {
  try {
    const raw = localStorage.getItem(PASSKEY_HANDLE_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed.credentialId === 'string' && typeof parsed.salt === 'string') {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

export function savePasskeyHandle(handle: PasskeyHandle): void {
  localStorage.setItem(PASSKEY_HANDLE_STORAGE_KEY, JSON.stringify(handle))
}

export function clearPasskeyHandle(): void {
  localStorage.removeItem(PASSKEY_HANDLE_STORAGE_KEY)
}

export function hasRegisteredPasskey(): boolean {
  return loadPasskeyHandle() !== null
}

// --- PRF derivation ---------------------------------------------------------

function readPrfResult(cred: PublicKeyCredential): Uint8Array | null {
  const ext = cred.getClientExtensionResults() as any
  const first = ext?.prf?.results?.first
  if (!first) return null
  return new Uint8Array(first as ArrayBuffer)
}

/**
 * Register a new PRF-capable passkey and return both the (non-secret) handle to
 * persist and the derived `prfKey` (hex) to hand to `keystore.addSecret`.
 *
 * Must be called from a user gesture. Some browsers return the PRF result on
 * `create()`, many don't — in that case we immediately run a `get()` to obtain it.
 */
export async function registerPasskey(opts: {
  userId: string
  userName: string
}): Promise<{ handle: PasskeyHandle; prfKey: string }> {
  if (!isPasskeyPrfSupported()) {
    throw new Error('Passkeys are not supported in this browser/context.')
  }

  const salt = crypto.getRandomValues(new Uint8Array(32))
  const challenge = crypto.getRandomValues(new Uint8Array(32))

  const cred = (await navigator.credentials.create({
    publicKey: {
      rp: { name: RP_NAME },
      user: {
        id: asBufferSource(utf8ToBytes(opts.userId)),
        name: opts.userName,
        displayName: opts.userName
      },
      challenge: asBufferSource(challenge),
      // ES256 then RS256 — broadest authenticator coverage.
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 }
      ],
      authenticatorSelection: {
        userVerification: 'required',
        residentKey: 'required'
      },
      timeout: 60_000,
      extensions: { prf: { eval: { first: salt } } } as any
    }
  })) as PublicKeyCredential | null

  if (!cred) throw new Error('Passkey registration was cancelled.')

  const handle: PasskeyHandle = {
    credentialId: bufToB64url(cred.rawId),
    salt: bytesToHex(salt)
  }

  const fromCreate = readPrfResult(cred)
  if (fromCreate) {
    return { handle, prfKey: bytesToHex(fromCreate) }
  }

  // Browser didn't surface PRF on create — derive it with a follow-up assertion.
  const prfKey = await getPasskeyPrf(handle)
  return { handle, prfKey }
}

/**
 * Re-derive the stable PRF secret (hex) for an existing handle. Must be called
 * from a user gesture. Throws if the authenticator doesn't return a PRF result
 * (PRF unsupported — the caller must fall back to the password).
 */
export async function getPasskeyPrf(handle: PasskeyHandle): Promise<string> {
  if (!isPasskeyPrfSupported()) {
    throw new Error('Passkeys are not supported in this browser/context.')
  }

  const salt = hexToBytes(handle.salt)
  const challenge = crypto.getRandomValues(new Uint8Array(32))

  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: asBufferSource(challenge),
      allowCredentials: [{ type: 'public-key', id: b64urlToBuf(handle.credentialId) }],
      userVerification: 'required',
      timeout: 60_000,
      extensions: { prf: { eval: { first: salt } } } as any
    }
  })) as PublicKeyCredential | null

  if (!assertion) throw new Error('Passkey unlock was cancelled.')

  const prf = readPrfResult(assertion)
  if (!prf) {
    throw new Error('This authenticator does not support PRF — use your password instead.')
  }
  return bytesToHex(prf)
}
